import dayjs from "dayjs";

/**
 * 相対時間を計算して日本語で返す関数
 * @param {Date | string} date 対象の日時
 * @return {string} 相対時間の文字列（例：「3時間前」）
 */
export function timeAgo(date: Date | string): string {
  const now = dayjs();
  const targetDate = dayjs(date);
  const diff = now.diff(targetDate, "second"); // 秒差

  if (diff < 60) return `${diff}秒前`;
  if (diff < 3600) return `${Math.floor(diff / 60)}分前`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}時間前`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}日前`;
  if (diff < 2592000) return `${Math.floor(diff / 604800)}週間前`;
  if (diff < 31536000) return `${Math.floor(diff / 2592000)}カ月前`;
  return `${Math.floor(diff / 31536000)}年前`;
}

/**
 * 反応のあった日を「今日 / 昨日 / 一昨日 / N 日前」で返す。
 *
 * 経過ミリ秒を 24 時間で割るのではなく**暦日の差**で数える。営業が見るのは
 * 「今日の反応か」であって「24 時間以内か」ではないため、昨夜 23 時の反応は
 * 経過 10 時間でも「昨日」と出したい。
 * @param {Date | string} date 対象の日時
 * @return {string} 表示ラベル（例：「昨日」「5 日前」）
 */
export function formatRelativeDay(date: Date | string): string {
  const days = calendarDaysAgo(date);
  if (days <= 0) return "今日";
  if (days === 1) return "昨日";
  if (days === 2) return "一昨日";
  return `${days} 日前`;
}

/**
 * 直近 3 日だけ語で、それより前は月日で日時を返す（例：「昨日 23:41」「08/12 09:22」）。
 *
 * 時刻を落とさないのは、宛先一覧が「最終反応の新しい順」で並ぶため。1 配信の
 * 反応は送信から数日に集中するので、日付だけにすると大半の行が「今日」に潰れて
 * 並び順の理由が画面から読めなくなる。時刻そのものも、いつ架電するかの判断材料。
 * @param {Date | string | null | undefined} date 対象の日時
 * @param {string} [fallback] 日時が無いときの表示文字（既定は `—`）
 * @return {string} 表示ラベル
 */
export function formatRelativeDayTime(
  date: Date | string | null | undefined,
  fallback = "—",
): string {
  if (!date) return fallback;
  const time = dayjs(date).format("HH:mm");
  const days = calendarDaysAgo(date);
  if (days <= 0) return `今日 ${time}`;
  if (days === 1) return `昨日 ${time}`;
  if (days === 2) return `一昨日 ${time}`;
  return formatShortDateTime(date);
}

/**
 * 今日から見て何暦日前かを返す（未来は 0）。
 * @param {Date | string} date 対象の日時
 * @return {number} 暦日の差
 */
function calendarDaysAgo(date: Date | string): number {
  return dayjs().startOf("day").diff(dayjs(date).startOf("day"), "day");
}

/**
 * 絶対日時をフォーマットして返す関数
 * @param {Date | string} date 対象の日時
 * @return {string} フォーマットされた絶対日時（例：「2025/01/15 14:30:25」）
 */
export function formatAbsoluteTime(date: Date | string): string {
  return dayjs(date).format("YYYY/MM/DD HH:mm:ss");
}

/**
 * 指定した書式で日時を返す関数を作る。
 *
 * 書式だけが違うフォーマッタを個別に書くと、`fallback` の既定値や
 * 「日時が無いときは書式に通さない」という約束が本数ぶん複製される。
 * ここ 1 か所に閉じておけば、増やすときも書式文字列を選ぶだけで済む。
 * @param {string} pattern dayjs の書式文字列
 * @return {Function} 日時フォーマッタ（日時と fallback を受け取る）
 */
const formatWith =
  (pattern: string) =>
  (date: Date | string | null | undefined, fallback = "—"): string =>
    date ? dayjs(date).format(pattern) : fallback;

/**
 * 分までの日時表記 (YYYY/MM/DD HH:mm) を返す関数。
 * @param {Date | string | null | undefined} date 対象の日時
 * @param {string} [fallback] 日時が無いときの表示文字（既定は `—`）
 * @return {string} フォーマットされた日時
 */
export const formatDateTime = formatWith("YYYY/MM/DD HH:mm");

/**
 * 月日だけの表記 (MM/DD) を返す関数。グラフの横軸ラベル等の狭い場所向け。
 * @param {Date | string | null | undefined} date 対象の日時
 * @param {string} [fallback] 日時が無いときの表示文字（既定は `—`）
 * @return {string} フォーマットされた月日
 */
export const formatMonthDay = formatWith("MM/DD");

/**
 * 短縮日時表記 (MM/DD HH:mm) を返す関数。タイムライン等の狭いカラム向け。
 * @param {Date | string | null | undefined} date 対象の日時
 * @param {string} [fallback] 日時が無いときの表示文字（既定は `—`）
 * @return {string} 短縮日時表記
 */
export const formatShortDateTime = formatWith("MM/DD HH:mm");

/**
 * 相対時間と絶対時間の両方を管理する型
 */
export type TimeDisplay = {
  relative: string;
  absolute: string;
};

/**
 * 相対時間と絶対時間の両方を計算して返す関数
 * @param {Date | string} date 対象の日時
 * @return {TimeDisplay} 相対時間と絶対時間のオブジェクト
 */
export function getTimeDisplay(date: Date | string): TimeDisplay {
  return {
    relative: timeAgo(date),
    absolute: formatAbsoluteTime(date),
  };
}
